import { useEffect } from "react";
import { applySeo, clinicJsonLd, markOffSite, organizationJsonLd, websiteJsonLd } from "../lib/seoHead";
import { useSeoSettings } from "../lib/useSeoSettings";

/** Site-wide Organization / WebSite / clinic structured data, mounted once. */
export const BrandSchema = () => {
    const conf = useSeoSettings();

    useEffect(() => {
        if (!conf.brand_name) return undefined;
        const blocks = [organizationJsonLd(conf), websiteJsonLd(conf), clinicJsonLd(conf)].filter(Boolean);
        const nodes = blocks.map((data) => {
            const el = document.createElement("script");
            el.type = "application/ld+json";
            el.dataset.rhmBrand = "1";
            el.textContent = JSON.stringify(data);
            document.head.appendChild(el);
            return el;
        });
        return () => nodes.forEach((n) => n.remove());
    }, [conf]);

    // Keep the preview environment out of Google — only the configured site_url gets indexed.
    useEffect(() => {
        if (!conf.site_url) return;
        try {
            const live = new URL(conf.site_url).host;
            const here = window.location.host;
            const isPreviewHost = /\.preview\.emergentagent\.com$|\.emergent\.host$|^localhost/.test(here);
            if (isPreviewHost && here !== live) {
                markOffSite(true);
                applySeo({ noindex: true });
            }
        } catch { /* a malformed site_url should never break the page */ }
    }, [conf.site_url]);

    // Search Console verification: the owner pastes the whole <meta …> tag, we keep just the token.
    useEffect(() => {
        const raw = conf.verification_tag || "";
        const token = raw.includes("content=") ? (raw.match(/content=["']([^"']+)["']/) || [])[1] : raw.trim();
        if (!token) return undefined;
        const el = document.createElement("meta");
        el.name = "google-site-verification";
        el.content = token;
        document.head.appendChild(el);
        return () => el.remove();
    }, [conf.verification_tag]);

    // The site icon (favicon) is swappable from CRM → SEO & Google without a code change.
    useEffect(() => {
        const url = conf.favicon_url || "";
        if (!url) return undefined;
        const type = /\.svg($|\?)/i.test(url) ? "image/svg+xml"
            : /\.ico($|\?)/i.test(url) ? "image/x-icon" : "image/png";
        document.head.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')
            .forEach((el) => el.remove());
        const icon = document.createElement("link");
        icon.rel = "icon";
        icon.type = type;
        icon.href = url;
        const apple = document.createElement("link");
        apple.rel = "apple-touch-icon";
        apple.href = url;
        document.head.append(icon, apple);
        return () => [icon, apple].forEach((n) => n.remove());
    }, [conf.favicon_url]);

    return null;
};

export default BrandSchema;
